import React, { useState, useEffect, useRef, useContext } from 'react'
import { supabase } from '../lib/supabase'
import { ShowContext } from '../App'

// ============================================================
// MOTOR DE TRANSPOSIÇÃO (igual ao CifraCard)
// ============================================================
const NOTAS_SHARP = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B']
const NOTAS_FLAT  = ['C','Db','D','Eb','E','F','Gb','G','Ab','A','Bb','B']
const TONS_PT = {
  'Dó (C)':'C','Dó# (C#)':'C#','Ré (D)':'D','Ré# (D#)':'D#',
  'Mi (E)':'E','Fá (F)':'F','Fá# (F#)':'F#','Sol (G)':'G',
  'Sol# (G#)':'G#','Lá (A)':'A','Lá# (A#)':'A#','Si (B)':'B'
}
const TONS_LISTA = [
  'Dó (C)','Dó# (C#)','Ré (D)','Ré# (D#)','Mi (E)','Fá (F)',
  'Fá# (F#)','Sol (G)','Sol# (G#)','Lá (A)','Lá# (A#)','Si (B)'
]

function notaParaIdx(nota) {
  let idx = NOTAS_SHARP.indexOf(nota)
  if (idx === -1) idx = NOTAS_FLAT.indexOf(nota)
  return idx
}

function transporAcorde(acorde, semitons) {
  if (semitons === 0) return acorde
  return acorde.replace(/[A-G][#b]?/g, (nota) => {
    const idx = notaParaIdx(nota)
    if (idx === -1) return nota
    const novoIdx = ((idx + semitons) % 12 + 12) % 12
    const usaFlat = nota.includes('b') || [1,3,6,8,10].includes(novoIdx)
    return usaFlat ? NOTAS_FLAT[novoIdx] : NOTAS_SHARP[novoIdx]
  })
}

function calcularSemitons(tomOriginal, tomDestino) {
  const orig = TONS_PT[tomOriginal] || tomOriginal
  const dest = TONS_PT[tomDestino] || tomDestino
  const idxOrig = notaParaIdx(orig)
  const idxDest = notaParaIdx(dest)
  if (idxOrig === -1 || idxDest === -1) return 0
  return ((idxDest - idxOrig) % 12 + 12) % 12
}

function transporCifra(texto, semitons) {
  if (!texto || semitons === 0) return texto
  return texto.split('\n').map(linha => {
    const t = linha.trim()
    if (!t) return linha
    const isAcordeLinha = /^([A-G][#b]?[mM]?(7|maj7|maj|min|dim|aug|sus2|sus4|add9|6|9|11|13)?(\([^)]+\))?(\s*\/\s*[A-G][#b]?)?(\s+|$))+$/.test(t)
    if (isAcordeLinha) {
      return linha.replace(/[A-G][#b]?[mM]?(7|maj7|maj|min|dim|aug|sus2|sus4|add9|6|9|11|13)?(\([^)]+\))?(\s*\/\s*[A-G][#b]?)?/g, m => transporAcorde(m, semitons))
    }
    return linha
  }).join('\n')
}

function renderCifraTexto(texto, fontSize, modoVocal = false) {
  if (!texto) return null
  return texto.split('\n').map((linha, i) => {
    const t = linha.trim()
    const isSecao = /^(INTRO|VERSO|REFRÃO|PRÉ|PONTE|FINAL|BRIDGE|CODA|OUTRO|CHORUS|VERSE|PRIMEIRA|SEGUNDA|INTERLÚDIO)/i.test(t)
    const isAcorde = t.length > 0 && /^([A-G][#b]?[mM]?(7|maj7|maj|min|dim|aug|sus2|sus4|add9|6|9|11|13)?(\s*\/\s*[A-G][#b]?)?(\s+|$))+$/.test(t)
    // Modo vocal: oculta linhas que são só acordes
    if (modoVocal && isAcorde) return null
    return (
      <div key={i} style={{
        color: isSecao ? '#58a6ff' : isAcorde ? '#00CFFF' : 'rgba(255,255,255,0.88)',
        fontWeight: isSecao || isAcorde ? 700 : 400,
        fontSize: isSecao ? 11 : fontSize,
        textTransform: isSecao ? 'uppercase' : 'none',
        letterSpacing: isSecao ? 1.5 : 0,
        marginTop: isSecao ? 10 : 0,
        lineHeight: 1.65,
        minHeight: '1em'
      }}>
        {linha || '\u00A0'}
      </div>
    )
  })
}

// ============================================================
// BARRA DE TOM (reutilizável)
// ============================================================
function BarraTom({ tomAtual, tomOriginal, onChange }) {
  const [mostrar, setMostrar] = useState(false)
  const semitons = tomOriginal && tomAtual ? calcularSemitons(tomOriginal, tomAtual) : 0

  return (
    <div style={{ padding: '10px 16px', background: '#0c2040', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: mostrar ? 10 : 0 }}>
        {tomOriginal && (
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>
            Original: <strong style={{ color: 'rgba(255,255,255,0.6)' }}>{tomOriginal}</strong>
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => {
          const idx = TONS_LISTA.indexOf(tomAtual)
          onChange(TONS_LISTA[idx > 0 ? idx - 1 : 11])
        }} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
        <button onClick={() => setMostrar(s => !s)}
          style={{ background: 'rgba(0,207,255,0.15)', border: '2px solid rgba(0,207,255,0.5)', color: '#00CFFF', padding: '5px 14px', borderRadius: 50, fontSize: 13, fontWeight: 800, cursor: 'pointer', minWidth: 90, textAlign: 'center', fontFamily: 'Montserrat, sans-serif' }}>
          🎵 {tomAtual || 'Tom'}
        </button>
        <button onClick={() => {
          const idx = TONS_LISTA.indexOf(tomAtual)
          onChange(TONS_LISTA[idx < 11 ? idx + 1 : 0])
        }} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
      </div>

      {mostrar && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 4 }}>
          {TONS_LISTA.map(t => (
            <button key={t} onClick={() => { onChange(t); setMostrar(false) }}
              style={{ padding: '5px 10px', borderRadius: 50, border: `1px solid ${t === tomAtual ? '#00CFFF' : 'rgba(255,255,255,0.15)'}`, background: t === tomAtual ? 'rgba(0,207,255,0.2)' : 'rgba(255,255,255,0.05)', color: t === tomAtual ? '#00CFFF' : 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: t === tomAtual ? 700 : 400, cursor: 'pointer' }}>
              {t}
            </button>
          ))}
        </div>
      )}

      {semitons !== 0 && (
        <div style={{ fontSize: 11, color: 'rgba(0,207,255,0.7)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>↕ {semitons > 6 ? semitons - 12 : semitons} semitons</span>
          <button onClick={() => onChange(tomOriginal || '')}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
            Resetar
          </button>
        </div>
      )}
    </div>
  )
}

// ============================================================
// COMPONENTE PRINCIPAL — ShowAoVivoModal
//
// Props:
//   mode: 'controller' | 'viewer'
//
//   (controller only)
//   cifras: array de {id, cifra_id, ordem, tom_usado, cifras: {titulo, artista, conteudo_texto, tom_original, tom_ultima_vez, ...}}
//
//   (viewer only — passados pelo FeedPage via state do canal)
//   cifraAtual: objeto cifra
//   posicao: number
//   total: number
//   controladorNome: string
//
//   (ambos)
//   setlistNome: string
//   grupoId: string
//   userName: string  (nome do usuário atual)
//   onClose: fn
// ============================================================
export default function ShowAoVivoModal({
  mode,
  cifras,
  cifraAtual,
  tomUsado,      // tom que o admin está usando (só no viewer)
  posicao,
  total,
  controladorNome,
  setlistNome,
  grupoId,
  userName,
  modoVocal = false,
  onClose
}) {
  // Estado do controller
  const [pos, setPos] = useState(0)
  const [showLista, setShowLista] = useState(false)
  const [confirmandoFim, setConfirmandoFim] = useState(false)
  const channelRef = useRef(null)
  const retryTimersRef = useRef([])
  const showEncerradoRef = useRef(false)
  // Refs closure-safe para heartbeat e pedal
  const posRef = useRef(0)
  const tomAtualRef = useRef('')

  // Context do GrupoLayout — permite notificar o layout diretamente no dispositivo do controller
  const showCtx = useContext(ShowContext)

  // Estado compartilhado (tom e fonte — cada dispositivo controla localmente)
  const [tomAtual, setTomAtual] = useState('')
  const [fontSize, setFontSize] = useState(14)

  // Mantém refs atualizadas para uso em closures (heartbeat, pedal)
  useEffect(() => { posRef.current = pos }, [pos])
  useEffect(() => { tomAtualRef.current = tomAtual }, [tomAtual])

  // Viewer: sincroniza tom do admin (quando muda de música OU quando admin muda o tom)
  const prevCifraId = useRef(null)
  useEffect(() => {
    if (mode !== 'viewer') return
    if (cifraAtual && cifraAtual.id !== prevCifraId.current) {
      prevCifraId.current = cifraAtual.id
    }
    // Sempre usa o tom que o admin está enviando
    if (tomUsado) {
      setTomAtual(tomUsado)
    } else if (cifraAtual) {
      setTomAtual(cifraAtual.tom_ultima_vez || cifraAtual.tom_original || '')
    }
  }, [cifraAtual, tomUsado, mode])

  // Controller: canal próprio para broadcast + presence tracking
  useEffect(() => {
    if (mode !== 'controller' || !cifras?.length) return

    const primeiraC = cifras[0]?.cifras
    const tomInicial = primeiraC?.tom_ultima_vez || primeiraC?.tom_original || ''
    setTomAtual(tomInicial)
    showEncerradoRef.current = false

    const canal = supabase.channel(`show:${grupoId}`)
    channelRef.current = canal

    canal.subscribe(async (status) => {
      if (status !== 'SUBSCRIBED') return

      // Envia o início e notifica o layout local (broadcast não chega no mesmo socket)
      const payload = await enviarBroadcast(canal, 'inicio', 0, primeiraC, cifras.length, tomInicial)
      if (showCtx?.notificarInicio) showCtx.notificarInicio(payload)

      // Re-envia como 'inicio' após 2 s e 5 s para garantir chegada
      // (viewers que não receberam o primeiro ou que conectaram logo depois)
      const reenviar = () => {
        if (channelRef.current) enviarBroadcast(channelRef.current, 'inicio', 0, primeiraC, cifras.length, tomInicial)
      }
      retryTimersRef.current = [
        setTimeout(reenviar, 2000),
        setTimeout(reenviar, 5000),
      ]
    })

    return () => {
      retryTimersRef.current.forEach(clearTimeout)
      if (!showEncerradoRef.current && channelRef.current) {
        channelRef.current.send({
          type: 'broadcast', event: 'show_update', payload: { tipo: 'fim' }
        }).catch(() => {})
        channelRef.current.untrack().catch(() => {})
      }
      supabase.removeChannel(canal)
      channelRef.current = null
    }
  }, []) // mount once

  // Heartbeat: re-publica presence a cada 15 s para late-joiners pegarem o estado atual
  useEffect(() => {
    if (mode !== 'controller' || !cifras?.length) return
    const id = setInterval(() => {
      if (!channelRef.current || showEncerradoRef.current) return
      const c = cifras[posRef.current]?.cifras
      channelRef.current.track({
        tipo: 'controller',
        showState: {
          tipo: 'cifra',
          setlistNome,
          controladorNome: userName,
          cifraAtual: c,
          tomUsado: tomAtualRef.current,
          posicao: posRef.current,
          total: cifras.length
        }
      }).catch(() => {})
    }, 15000)
    return () => clearInterval(id)
  }, [mode, cifras, setlistNome, userName])

  // Pedal Bluetooth: maioria dos pedais emite teclas de teclado padrão
  useEffect(() => {
    if (mode !== 'controller') return
    function handleKeydown(e) {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault()
        irParaCifra(posRef.current + 1)
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault()
        irParaCifra(posRef.current - 1)
      }
    }
    window.addEventListener('keydown', handleKeydown)
    return () => window.removeEventListener('keydown', handleKeydown)
  }, [mode, cifras]) // cifras muda apenas 1x no mount

  async function enviarBroadcast(canal, tipo, novaPos, cifraObj, totalCifras, tomDoAdmin) {
    const payload = {
      tipo,
      setlistNome,
      controladorNome: userName,
      cifraAtual: cifraObj,
      tomUsado: tomDoAdmin,   // tom atual do admin — viewers sincronizam este
      posicao: novaPos,
      total: totalCifras
    }
    try {
      await canal.send({ type: 'broadcast', event: 'show_update', payload })
    } catch (e) {
      console.warn('[ShowAoVivo] broadcast error:', e)
    }
    // Atualiza Presence: qualquer viewer que abrir o app DEPOIS receberá este estado
    // imediatamente via evento 'sync', sem precisar de rebroadcast
    if (tipo !== 'fim') {
      try { await canal.track({ tipo: 'controller', showState: payload }) } catch (e) {}
    }
    return payload
  }

  // Admin muda o tom: atualiza local E transmite para todos os viewers
  async function mudarTomAdmin(novoTom) {
    setTomAtual(novoTom)
    if (channelRef.current) {
      await enviarBroadcast(
        channelRef.current, 'tom', pos,
        cifras[pos]?.cifras, cifras.length, novoTom
      )
    }
  }

  async function irParaCifra(novaPos) {
    if (novaPos < 0 || novaPos >= (cifras?.length || 0)) return
    const c = cifras[novaPos].cifras
    const novoTom = c?.tom_ultima_vez || c?.tom_original || ''
    setPos(novaPos)
    setTomAtual(novoTom)
    setShowLista(false)
    await enviarBroadcast(channelRef.current, 'cifra', novaPos, c, cifras.length, novoTom)
  }

  async function encerrarShow() {
    retryTimersRef.current.forEach(clearTimeout)
    showEncerradoRef.current = true
    if (channelRef.current) {
      await channelRef.current.send({
        type: 'broadcast', event: 'show_update', payload: { tipo: 'fim' }
      }).catch(() => {})
      await channelRef.current.untrack().catch(() => {})
      supabase.removeChannel(channelRef.current)
      channelRef.current = null
    }
    if (showCtx?.notificarFim) showCtx.notificarFim()
    onClose()
  }

  // Cifra a exibir
  const cifraObj = mode === 'controller' ? cifras?.[pos]?.cifras : cifraAtual
  const tomBase = tomAtual || cifraObj?.tom_ultima_vez || cifraObj?.tom_original || ''
  const semitons = cifraObj?.tom_original ? calcularSemitons(cifraObj.tom_original, tomBase) : 0
  const cifraTransposta = transporCifra(cifraObj?.conteudo_texto, semitons)
  const posAtual = mode === 'controller' ? pos : (posicao ?? 0)
  const totalCifras = mode === 'controller' ? (cifras?.length || 0) : (total ?? 0)

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      display: 'flex', flexDirection: 'column',
      maxWidth: 480, margin: '0 auto',
      background: '#0d1117'
    }}>

      {/* ── Banner AO VIVO ── */}
      <div style={{
        background: 'linear-gradient(135deg, #e07800, #FF8C00)',
        padding: '10px 16px',
        display: 'flex', alignItems: 'center', gap: 10,
        flexShrink: 0
      }}>
        <span className="ao-vivo-dot" />
        <span style={{ color: '#fff', fontWeight: 800, fontSize: 12, letterSpacing: 1.5, fontFamily: 'Montserrat, sans-serif' }}>AO VIVO</span>
        <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {setlistNome}
        </span>
        {totalCifras > 0 && (
          <span style={{ color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: 'Montserrat, sans-serif', background: 'rgba(0,0,0,0.25)', padding: '3px 10px', borderRadius: 50, flexShrink: 0 }}>
            {posAtual + 1} / {totalCifras}
          </span>
        )}
      </div>

      {/* ── Header da cifra ── */}
      <div style={{
        padding: '12px 16px',
        background: '#071629',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 10
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 18, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cifraObj?.titulo || '—'}
          </div>
          {cifraObj?.artista && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>{cifraObj.artista}</div>
          )}
          {mode === 'viewer' && controladorNome && (
            <div style={{ fontSize: 11, color: 'rgba(255,140,0,0.85)', marginTop: 3 }}>
              Controlado por <strong>{controladorNome}</strong>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={() => setFontSize(f => Math.max(10, f - 2))}
            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
            A-
          </button>
          <button onClick={() => setFontSize(f => Math.min(22, f + 2))}
            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
            A+
          </button>
        </div>
      </div>

      {/* ── Barra de tom ── */}
      {/* Controller: muda localmente E transmite para viewers */}
      {/* Viewer: pode mudar localmente (sobrescreve tom do admin) */}
      <BarraTom
        tomAtual={tomBase}
        tomOriginal={cifraObj?.tom_original}
        onChange={mode === 'controller' ? mudarTomAdmin : setTomAtual}
      />

      {/* ── Conteúdo da cifra ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', WebkitOverflowScrolling: 'touch' }}>
        {cifraTransposta ? (
          <div style={{
            fontFamily: "'Courier New', monospace",
            background: 'rgba(0,0,0,0.3)',
            borderRadius: 12,
            padding: '16px 14px',
            border: '1px solid rgba(255,255,255,0.07)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {renderCifraTexto(cifraTransposta, fontSize, modoVocal)}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(255,255,255,0.3)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎵</div>
            <div>
              {mode === 'viewer'
                ? 'Aguardando próxima música...'
                : 'Nenhuma cifra nesta posição'}
            </div>
          </div>
        )}
        <div style={{ height: 50 }} />
      </div>

      {/* ── Lista de músicas (controller) ── */}
      {mode === 'controller' && showLista && (
        <div style={{
          position: 'absolute', bottom: 96, left: 0, right: 0,
          background: '#071629',
          borderTop: '1px solid rgba(255,255,255,0.12)',
          maxHeight: '45vh', overflowY: 'auto',
          zIndex: 10
        }}>
          <div style={{ padding: '10px 16px 4px', fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Músicas do setlist
          </div>
          {cifras.map((item, idx) => {
            const c = item.cifras
            const ativo = idx === pos
            return (
              <div key={item.id || idx}
                onClick={() => irParaCifra(idx)}
                style={{
                  padding: '12px 16px',
                  display: 'flex', alignItems: 'center', gap: 12,
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  background: ativo ? 'rgba(255,140,0,0.12)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'background 0.15s'
                }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: ativo ? '#FF8C00' : 'rgba(255,255,255,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0
                }}>
                  {idx + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: ativo ? 700 : 600, color: ativo ? '#FF8C00' : '#f0f0f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c?.titulo || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{c?.artista}</div>
                </div>
                {(item.tom_usado || c?.tom_ultima_vez) && (
                  <span style={{ fontSize: 11, color: '#00CFFF', fontWeight: 700, flexShrink: 0 }}>
                    {item.tom_usado || c?.tom_ultima_vez}
                  </span>
                )}
                {ativo && <span style={{ color: '#FF8C00', fontSize: 16, flexShrink: 0 }}>▶</span>}
              </div>
            )
          })}
        </div>
      )}

      {/* ── Controles do Controller ── */}
      {mode === 'controller' && (
        <div style={{
          padding: '8px 16px 32px',
          background: '#071629',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          flexShrink: 0
        }}>
          <button
            onClick={() => setShowLista(s => !s)}
            style={{
              width: '100%', padding: '8px', borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.1)',
              background: showLista ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)',
              color: 'rgba(255,255,255,0.65)', fontSize: 12, cursor: 'pointer',
              marginBottom: 8
            }}>
            {showLista ? '▼ Fechar lista' : `▲ Ver todas as músicas (${cifras?.length || 0})`}
          </button>

          {confirmandoFim ? (
            /* Confirmação de encerramento */
            <div style={{ padding: '12px 4px 4px' }}>
              <div style={{ textAlign: 'center', fontSize: 14, color: '#fff', fontWeight: 600, marginBottom: 12 }}>
                ⏹ Encerrar o show para todos?
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setConfirmandoFim(false)}
                  style={{
                    flex: 1, padding: 13, borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.07)',
                    color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 600, cursor: 'pointer'
                  }}>
                  Cancelar
                </button>
                <button
                  onClick={encerrarShow}
                  style={{
                    flex: 1, padding: 13, borderRadius: 12, border: 'none',
                    background: 'linear-gradient(135deg, #c0392b, #e74c3c)', color: '#fff',
                    fontSize: 14, fontWeight: 700, cursor: 'pointer'
                  }}>
                  ✓ Sim, encerrar
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => irParaCifra(pos - 1)}
                disabled={pos === 0}
                style={{
                  flex: 1, padding: 13, borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: pos === 0 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)',
                  color: pos === 0 ? 'rgba(255,255,255,0.2)' : '#fff',
                  fontSize: 15, fontWeight: 700,
                  cursor: pos === 0 ? 'default' : 'pointer',
                  transition: 'all 0.2s'
                }}>
                ‹ Anterior
              </button>

              <button
                onClick={() => setConfirmandoFim(true)}
                style={{
                  flex: 1, padding: 13, borderRadius: 12, border: 'none',
                  background: 'rgba(255,140,0,0.18)', color: '#FF8C00',
                  border: '1px solid rgba(255,140,0,0.35)',
                  fontSize: 14, fontWeight: 700, cursor: 'pointer'
                }}>
                ⏹ Encerrar
              </button>

              <button
                onClick={() => irParaCifra(pos + 1)}
                disabled={pos >= (cifras?.length || 1) - 1}
                style={{
                  flex: 1, padding: 13, borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: pos >= (cifras?.length || 1) - 1 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)',
                  color: pos >= (cifras?.length || 1) - 1 ? 'rgba(255,255,255,0.2)' : '#fff',
                  fontSize: 15, fontWeight: 700,
                  cursor: pos >= (cifras?.length || 1) - 1 ? 'default' : 'pointer',
                  transition: 'all 0.2s'
                }}>
                Próxima ›
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Controles do Viewer ── */}
      {mode === 'viewer' && (
        <div style={{
          padding: '10px 16px 32px',
          background: '#071629',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          flexShrink: 0
        }}>
          <div style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 10 }}>
            🔴 Acompanhando em tempo real · cada cifra atualiza automaticamente
          </div>
          <button
            onClick={onClose}
            style={{
              width: '100%', padding: 13, borderRadius: 12,
              border: '1px solid rgba(255,140,0,0.3)',
              background: 'rgba(255,140,0,0.08)',
              color: 'rgba(255,140,0,0.85)', fontSize: 14, fontWeight: 600, cursor: 'pointer'
            }}>
            ↙ Minimizar — o show continua
          </button>
        </div>
      )}
    </div>
  )
}
